import * as React from "react"
import { useAccounts } from "@/features/accounts/hooks/useAccounts"
import { useAllTransactions, useAvailableMonths } from "@/features/transactions/hooks/useTransactions"
import { useCategories } from "@/features/transactions/hooks/useCategories"
import type { Account, Category } from "@/types"
import type { CollectionTransaction } from "../types"

export interface CarteraDataOptions {
  year?: number
  month?: number
}

export function useCarteraData(options?: CarteraDataOptions) {
  const queryParams = React.useMemo(() => {
    if (!options?.year || !options?.month) return undefined
    return { year: options.year, month: options.month }
  }, [options?.year, options?.month])

  const { data: accounts = [], isLoading: isLoadingAccounts } = useAccounts()
  const {
    data: transactions = [],
    isLoading: isLoadingTransactions,
    isFetching: isFetchingTransactions,
  } = useAllTransactions(queryParams)
  const { data: categories = [], isLoading: isLoadingCategories } = useCategories()
  const { data: availableMonths = [] } = useAvailableMonths()

  const transactionsByType = React.useMemo(() => {
    const typed = transactions as CollectionTransaction[]
    const expenses: CollectionTransaction[] = []
    const incomes: CollectionTransaction[] = []
    const savings: CollectionTransaction[] = []

    for (const tx of typed) {
      if (tx._type === "EXPENSE") {
        expenses.push(tx)
      } else if (tx._type === "INCOME") {
        incomes.push(tx)
      } else if (tx._type === "SAVING") {
        savings.push(tx)
      }
    }

    return {
      EXPENSE: expenses,
      INCOME: incomes,
      SAVING: savings,
    }
  }, [transactions])

  const accountsById = React.useMemo(
    () => new Map<number, Account>(accounts.map((account) => [account.id, account])),
    [accounts],
  )

  const categoriesById = React.useMemo(
    () => new Map<number, Category>(categories.map((category) => [category.id, category])),
    [categories],
  )

  return {
    accounts,
    transactions,
    categories,
    availableMonths,
    transactionsByType,
    accountsById,
    categoriesById,
    isLoading: isLoadingAccounts || isLoadingTransactions || isLoadingCategories,
    isLoadingTransactions,
    isFetchingTransactions,
    isFetching: isFetchingTransactions,
  }
}


